<script setup lang="ts">
import { computed, nextTick, ref, useCssModule, watch } from "vue";

import Modal from "@/components/modal/modal.vue";
import StepsButtons from "@/components/StepsButtons/StepsButtons.vue";
import { deviceGradeNormalize } from "@/helpers/deviceGrade";
import { apiGetQuestions } from "@/hooks/requests/apiGetQuestions/apiGetQuestions";
import useTradeInCalculation from "@/hooks/useTradeInCalculation/useTradeInCalculation";
import useTranslate from "@/hooks/useTranslate/useTranslate";
import { useHowYouRateDeviceStore } from "@/stores/howYouRateDevice/howYouRateDevice";
import { GRADES_DATA } from "@/stores/howYouRateDevice/howYouRateDevice.model";
import { I_HowYouRateDevice } from "@/stores/howYouRateDevice/howYouRateDevice.types";
import { useTIWStore } from "@/stores/tiw/tiw";
import Button from "@/ui/button/button.vue";
import { RadioSkeleton } from "@/ui/skeletons";

const classes = useCssModule();

const howYouRateDeviceStore = useHowYouRateDeviceStore();

const prevFlow = ref<I_HowYouRateDevice["questionData"][]>([]);
const questionData = ref<I_HowYouRateDevice["questionData"]>(
    howYouRateDeviceStore.discountLaterFirstQuestion,
);
const answers = ref<string[]>([]);
const isLoading = ref<boolean>(false);

const TIWStore = useTIWStore();

const isMultiSelect = computed(() => {
    return questionData.value.answerType === "MULTISELECT";
});

const isYesNoQuestion = computed(() => {
    return questionData.value.answerType === "YN";
});

watch(answers, () => console.log(answers.value), { deep: true });

const setAnswer = (event: Event) => {
    const target = event.target as HTMLInputElement;

    if (answers.value.includes(target.value)) {
        answers.value = answers.value.filter((el) => el !== target.value);
        return;
    }
    if (isMultiSelect.value) {
        answers.value.push(target.value);
    } else {
        answers.value = [];
        answers.value.push(target.value);
    }
};

function onBack() {
    function doOnBack() {
        const prev = prevFlow.value.pop();
        if (!prev) {
            TIWStore.switchStep("howYouRateDevice");
            return;
        }

        questionData.value = prev;
    }

    if (document.startViewTransition) {
        document.startViewTransition(async () => {
            doOnBack();
            await nextTick();
        });
    } else {
        doOnBack();
    }
}

const getQuestionData = async (payload: { ans: string; c: string }) => {
    isLoading.value = true;
    if (questionData.value) prevFlow.value.push(questionData.value);

    questionData.value = await apiGetQuestions({
        ...payload,
        deviceId: howYouRateDeviceStore.registeringDeviceData.deviceId,
        lang: TIWStore.standardizedLocale,
        questionId: questionData.value?.questionId ?? "",
        token: TIWStore.WIDGET_DATA.token,
    });
    isLoading.value = false;
};

async function onNext(yesNoAnswer?: "Y" | "N") {
    async function doNext() {
        isLoading.value = true;
        const joinedAnswer = answers.value.join();
        if (questionData.value.last) {
            await getQuestionData({ c: joinedAnswer, ans: yesNoAnswer });
            TIWStore.switchStep("successfulEvaluation");
            howYouRateDeviceStore.grade = deviceGradeNormalize(questionData.value.device.grade);
            howYouRateDeviceStore.rate = useTranslate(
                `tiw__grades__${GRADES_DATA[questionData.value.device.grade].gradeCode}_stateInfo`,
            );
            useTradeInCalculation({
                tradeInPrice: questionData.value.device.price,
            });
            isLoading.value = false;
            return;
        }
        await getQuestionData({ c: joinedAnswer, ans: yesNoAnswer });
        answers.value = [];
        isLoading.value = false;
    }

    if (document.startViewTransition) {
        document.startViewTransition(async () => {
            await doNext();
            await nextTick();
        });
    } else {
        doNext();
    }
}
</script>

<template>
    <Modal>
        <template #header>
            <p class="tiw__h2">{{ useTranslate("tiw__visualEvaluation__header") }}</p>
        </template>
        <div v-if="questionData">
            <b>{{ questionData.question }}</b>
            <p :class="classes.desc" v-html="questionData.description" />
            <div v-if="questionData.options" :class="classes.options">
                <div v-for="option in questionData.options" :key="option.cid" :class="classes.item">
                    <input
                        :id="option.cid"
                        :class="classes.input"
                        :value="option.cid"
                        type="checkbox"
                        :checked="answers.includes(option.cid)"
                        name="options"
                        @change="setAnswer"
                    />
                    <label :class="classes.label" :for="option.cid">
                        <span :class="classes.cycle">
                            <div :class="classes.dot"></div>
                        </span>
                        <div>
                            {{ option.cname }}
                            <div :class="classes.desc" v-html="option.description" />
                        </div>
                    </label>
                </div>
            </div>

            <StepsButtons
                :loading="isLoading"
                :disabled="isYesNoQuestion ? isLoading : !answers.length || isLoading"
                :btn-text="isYesNoQuestion ? 'Tak' : useTranslate('tiw__continue')"
                :click-to-next="isYesNoQuestion ? () => onNext('Y') : () => onNext()"
                :on-back="onBack"
            >
                <template #additionalBtn="{ loading }">
                    <Button
                        v-if="isYesNoQuestion"
                        variant="basic"
                        color="light"
                        :loading="loading"
                        :disabled="isYesNoQuestion ? isLoading : !answers.length || isLoading"
                        @click="isYesNoQuestion ? onNext('N') : onNext()"
                    >
                        {{ useTranslate("tiw__visualEvaluation__no") }}
                    </Button>
                </template>
            </StepsButtons>
        </div>
        <div v-else class="tiw__visualEvaluation__skeletons">
            <RadioSkeleton :count="8" />
        </div>
    </Modal>
</template>

<style module>
.desc {
    margin: 16px 0;
    color: #808d9a;
    font-size: 12px;

    li {
        list-style-position: inside;
    }
    ul {
        padding: 0;
        padding-left: 25px;
        margin-bottom: 0;
        margin-top: 8px;
        padding-inline-start: 0px;
    }
}
.options {
    display: flex;
    flex-direction: column;
    margin-top: 16px;
}
.item {
    display: flex;
    align-items: start;

    &:has(input:checked) {
        .dot {
            width: 12px;
            height: 12px;
            background: var(--custom-color-main);
            border-radius: 100%;
        }
    }
}
.input {
    width: 0px;
    margin: 0;
    flex-shrink: 0;
    height: 0px;
}
.label {
    font-size: 14px;
    display: flex;
    align-items: start;
    gap: 12px;
}
.cycle {
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border: 1px solid #808d9a;
    border-radius: 100%;
    flex-shrink: 0;
}
.dot {
    transition: all 0.3s;
    width: 0;
    height: 0;
}
</style>
